import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getActorDetails, getActorsbyMovie, getMoviesByActor, TMDB_IMG } from '../api/api';
import MovieCard from '../components/MovieCard';
import SkeletonCard from '../components/ui/SkeletonCard';
import StepCounter from '../components/ui/StepCounter';

const ActorMini = ({ actor, label, highlight }) => (
  <div className={`text-center flex-1 ${highlight ? 'opacity-100' : 'opacity-60'}`}>
    <p className={`text-[9px] font-black uppercase tracking-wider mb-1 ${highlight ? 'text-[#ff6b35]' : 'text-[#888]'}`}>
      {label}
    </p>
    {actor?.profile_path ? (
      <img
        src={`${TMDB_IMG}${actor.profile_path}`}
        alt={actor.name}
        className={`w-10 h-10 rounded-full object-cover mx-auto mb-1 border-2 ${highlight ? 'border-[#ff6b35]' : 'border-[#1a1a1a]'}`}
      />
    ) : (
      <div className={`w-10 h-10 rounded-full bg-[#f0e6d3] mx-auto mb-1 flex items-center justify-center text-lg border-2 ${highlight ? 'border-[#ff6b35]' : 'border-[#1a1a1a]'}`}>
        🎭
      </div>
    )}
    <p className={`text-[10px] font-bold ${highlight ? 'text-[#ff6b35]' : 'text-[#1a1a1a]'}`}>
      {actor?.name ?? '?'}
    </p>
  </div>
);

const Ingame = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const startActor = location.state?.startActor ?? null;
  const endActor = location.state?.endActor ?? null;

  const [hops, setHops] = useState(0);
  const [path, setPath] = useState([]);
  const [pendingMovie, setPendingMovie] = useState(null);
  const [itemList, setItemList] = useState(null);
  const [filterItem, setFilterItem] = useState(null);
  const [showActors, setShowActors] = useState(false);
  const [actualActor, setActualActor] = useState(null);
  const [previousMovieList, setPreviousMovieList] = useState(null);
  const [searchItem, setSearchItem] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryMovieId, setRetryMovieId] = useState(null);

  const loadMovies = useCallback(async (actorId) => {
    setLoading(true);
    setError(false);
    setRetryMovieId(null);
    const data = await getMoviesByActor(actorId);
    if (data) {
      setItemList(data);
      setFilterItem(data);
      setPreviousMovieList(data);
      setShowActors(false);
    } else {
      setError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!startActor || !endActor) {
      navigate('/', { replace: true });
      return;
    }
    setActualActor(startActor);
    loadMovies(startActor.id);
  }, [startActor, endActor, navigate, loadMovies]);

  if (!startActor || !endActor) return null;

  const handleItemClick = async (id) => {
    if (loading) return;

    if (!showActors) {
      const movie = itemList.find(m => m.id === id);
      setPendingMovie(movie);
      setLoading(true);
      setError(false);
      const actorData = await getActorsbyMovie(id);
      if (actorData) {
        setItemList(actorData);
        setFilterItem(actorData);
        setShowActors(true);
        setSearchItem('');
      } else {
        setError(true);
        setRetryMovieId(id);
        setPendingMovie(null);
      }
      setLoading(false);
    } else {
      setLoading(true);
      const actor = await getActorDetails(id);
      setLoading(false);
      if (!actor) return;

      const newPathEntry = { actor: actualActor, movie: pendingMovie };

      if (actor.name === endActor.name) {
        navigate('/win', {
          state: {
            hops: hops + 1,
            path: [...path, newPathEntry],
            startActor,
            endActor,
          },
        });
      } else {
        setHops(h => h + 1);
        setPath(p => [...p, newPathEntry]);
        setPendingMovie(null);
        setActualActor(actor);
        setSearchItem('');
        loadMovies(actor.id);
      }
    }
  };

  const handleBackToMovies = () => {
    setShowActors(false);
    setItemList(previousMovieList);
    setFilterItem(previousMovieList);
    setPendingMovie(null);
    setSearchItem('');
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchItem(term);
    if (showActors) {
      setFilterItem(itemList.filter(a => a.name.toLowerCase().includes(term.toLowerCase())));
    } else {
      setFilterItem(itemList.filter(m => m.title.toLowerCase().includes(term.toLowerCase())));
    }
  };

  const instruction = showActors
    ? `Pick an actor from this movie`
    : `Pick a movie featuring ${actualActor?.name ?? ''}`;

  return (
    <div className="min-h-screen bg-[#fdf6ec] flex flex-col">

      <div className="bg-[#1a1a1a] px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <span className="bg-[#ff6b35] text-white text-[10px] font-black tracking-[3px] uppercase px-2.5 py-1 rounded">
          FILMQUEST
        </span>
        <StepCounter hops={hops} />
        <button
          onClick={() => navigate('/')}
          className="text-[#ff6b35] text-xs font-bold border-2 border-[#ff6b35] rounded-md px-2.5 py-1"
        >
          Surrender
        </button>
      </div>

      <div className="flex-1 px-4 py-4 max-w-sm mx-auto w-full">

        <div className="bg-white border-[3px] border-[#1a1a1a] rounded-xl px-4 py-3 shadow-[4px_4px_0_#1a1a1a] mb-4 flex items-center">
          <ActorMini actor={startActor} label="Start" highlight={false} />
          <span className="text-[#ccc] text-xl font-black mx-1">→</span>
          <ActorMini actor={actualActor} label="Now" highlight={true} />
          <span className="text-[#ccc] text-xl font-black mx-1">→</span>
          <ActorMini actor={endActor} label="Goal" highlight={false} />
        </div>

        <p className="text-sm font-bold text-[#1a1a1a] mb-3">{instruction}</p>

        <input
          type="text"
          value={searchItem}
          onChange={handleSearch}
          placeholder={showActors ? '🔍 Search actors...' : '🔍 Search movies...'}
          className="w-full px-3 py-2 border-[3px] border-[#1a1a1a] rounded-lg text-sm font-semibold bg-white mb-3 outline-none"
        />

        {showActors && (
          <button
            onClick={handleBackToMovies}
            className="mb-3 bg-white text-[#1a1a1a] font-black text-sm py-2 px-4 rounded-lg border-[3px] border-[#1a1a1a] shadow-[3px_3px_0_#1a1a1a] hover:shadow-[1px_1px_0_#1a1a1a] transition-shadow"
          >
            ← Back to movies
          </button>
        )}

        {error && (
          <div className="bg-white border-[3px] border-[#1a1a1a] rounded-xl p-4 text-center shadow-[3px_3px_0_#1a1a1a] mb-3">
            <p className="text-sm font-bold text-[#1a1a1a] mb-2">Couldn't load — try again</p>
            <button
              onClick={() => retryMovieId ? handleItemClick(retryMovieId) : loadMovies(actualActor.id)}
              className="bg-[#ff6b35] text-white font-black text-sm py-2 px-4 rounded-lg border-2 border-[#1a1a1a]"
            >
              Retry
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} variant="movie" />)}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {filterItem?.filter((item, idx, arr) => arr.findIndex(x => x.id === item.id) === idx).map(item => (
              <MovieCard
                key={item.id}
                itemId={item.id}
                itemTitle={showActors ? item.name : item.title}
                itemRole={showActors ? item.known_for_department : item.character}
                itemImgUrl={showActors ? item.profile_path : item.poster_path}
                isActor={showActors}
                onClickFunction={handleItemClick}
              />
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default Ingame;
